import {useCallback, useEffect, useState} from "react";
import {useResourceManagement} from "../hooks/resourceHooks";
import {getCompanyMembers} from "../services/companyMemberService";
import {swallowException} from "../utils/appUtils";
import {isResourceSucceeded} from "../utils/resourceUtils";
import {getInvitationsForCompany} from "../services/invitationService";
import {UserInvitationEx, UserInvitationStatus} from "../types/domain/userInvitation";
import {CompanyMemberItem} from "../types/companyMemberItem";
import {CompanyMemberEx} from "../types/domain/companyMember";
import {useCompany} from "./companiesHooks";

/**
 * Fetches the list of active Company Member items, including Company Members and Invitations
 */
export const useActiveCompanyMemberItems = () => {
    const [memberItems, setMemberItems] = useState<CompanyMemberItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const companyState = useCompany();
    const company = companyState.isSuccess && companyState.data !== undefined ? companyState.data : undefined;

    const membersManagement = useResourceManagement({getCompanyMembers});
    const invitesManagement = useResourceManagement({getInvitationsForCompany});
    const {getCompanyMembers: getMembers, resource: members} = membersManagement;
    const {getInvitationsForCompany: getInvitations, resource: invitations} = invitesManagement;

    const refresh = useCallback(() => {
        if (company) {
            getMembers(company.id).catch(swallowException);
            getInvitations(company.id).catch(swallowException);
        }
    }, [getMembers, getInvitations, company]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (company && (isResourceSucceeded(members) || isResourceSucceeded(invitations))) {
            setMemberItems([
                ...(isResourceSucceeded(members)
                    ? members.data.map((member) => companyMemberToMemberItem(member))
                    : []),
                ...(isResourceSucceeded(invitations)
                    ? invitations.data
                          .filter((item) => item.status === UserInvitationStatus.PENDING)
                          .map(invitationToMemberItem)
                    : []),
            ]);
            setIsLoading(false);
        }
    }, [members, invitations, setMemberItems, company, setIsLoading]);

    return {memberItems, isLoading, refresh};
};

const companyMemberToMemberItem = (member: CompanyMemberEx): CompanyMemberItem => ({
    membershipType: "Member",
    role: member.role,
    attributes: member.attributes,
    member,
});

const invitationToMemberItem = (invitation: UserInvitationEx): CompanyMemberItem => ({
    membershipType: "Invited",
    role: invitation.role,
    attributes: {},
    invitation,
});
